// Platform detection helpers for the notifications layer.
//
// ninefinds.com is wrapped in native WebViews (Android + iOS), which cannot do
// web push or service workers. On those platforms, push is delivered by native
// FCM code instead, so the web app must detect the WebView and step aside for
// the push parts (while still running Firestore listeners, which work fine).

/**
 * True when running inside the NineFinds Android WebView wrapper.
 *
 * Primary signal: the Android app appends a marker to the WebView User-Agent
 * (e.g. "NineFindsApp/Android"). The "; wv)" token is a heuristic fallback for
 * a generic Android WebView.
 */
export function isAndroidWebView(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/NineFindsApp\/Android/i.test(ua)) return true;
  return /Android/i.test(ua) && /;\s*wv\)/i.test(ua);
}

/**
 * True when running inside the NineFinds iOS (WKWebView) wrapper.
 *
 * Unlike Android there's no reliable UA "wv" token for WKWebView, so we rely on
 * the marker the iOS app appends to the User-Agent (e.g. "NineFindsApp/iOS").
 * The bridge presence is checked separately (see iosBridge.ts) where a live
 * handle is needed and the UA may not yet be set.
 */
export function isIosWebView(): boolean {
  if (typeof navigator === "undefined") return false;
  return /NineFindsApp\/iOS/i.test(navigator.userAgent || "");
}

/** Play Store listing of the NineFinds Android app. */
export const ANDROID_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.ninefinds.app";

/**
 * Oldest Android app build (versionCode) that can complete Cashfree autopay.
 * Builds before 7 hand Cashfree's checkout redirect to the phone's browser,
 * which drops the session ("subs_session_id is not present or is invalid").
 * Only raise this AFTER the new build is live on the Play Store.
 */
export const MIN_ANDROID_APP_VERSION_FOR_AUTOPAY = 7;

/**
 * The Android app's versionCode from its User-Agent marker
 * ("NineFindsApp/Android/7"). Builds before 7 sent no version: returns 0 for
 * those, and null outside the NineFinds Android app (including other apps' WebViews).
 */
export function getAndroidAppVersion(): number | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  // Only our own app carries the marker. Other apps' WebViews (Instagram, Gmail...)
  // also match isAndroidWebView(), and must never be gated.
  if (!/NineFindsApp\/Android/i.test(ua)) return null;
  const match = /NineFindsApp\/Android\/(\d+)/i.exec(ua);
  return match ? Number(match[1]) : 0;
}

/** True inside an Android app build too old to set up autopay. */
export function isAndroidAppTooOldForAutopay(): boolean {
  const version = getAndroidAppVersion();
  return version !== null && version < MIN_ANDROID_APP_VERSION_FOR_AUTOPAY;
}

/** True inside either native wrapper — where push is handled natively. */
export function isNativeWebView(): boolean {
  return isAndroidWebView() || isIosWebView();
}

/** True when the browser supports the web-push primitives FCM needs. */
export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}
